define('uikit/modules/input.currency',['require','exports','module','marionette','underscore','../mixins/component.mixin','../elements/input','templates','../helper/is.ignored.key'],function(require, exports, module) {
  //prettier-ignore
  var Mn             = require('marionette'),
      _              = require('underscore'),
      ComponentMixin = require('../mixins/component.mixin'),
      InputView      = require('../elements/input'),
      Templates      = require('templates'),
      isIgnoredKey   = require('../helper/is.ignored.key');

  var MAXIMUM_ERROR = 'Maximum amount of 999,999,999.99 exceeded.';
  var NONNUMERIC_ERROR = 'Numeric value must be in currency format.';

  var stripNumber = function(n) {
    // 1234567 -> 1,234,567
    return n.replace(/\D/g, '');
  };

  var formatNumber = function(n) {
    // 1234567 -> 1,234,567
    return stripNumber(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  var wholeCurrencyDigits = function(n) {
    // match all numbers before the decimal point
    var matchResult = n.replace(/[^\d\.]/g, '').match(/(\d+)\.?/);
    if (matchResult && matchResult[1]) {
      return matchResult[1].length;
    }
    return 0;
  };

  var InputCurrencyInputView = InputView.extend({
    className: 'ui input left icon fluid',

    options: _.extend({}, InputView.prototype.options, {
      allowNegative: true,
      inputType: 'text',
      icon: 'dollar',
      max: null,
    }),

    forceUpdate: _.extend({}, InputView.prototype.forceUpdate, {
      inputType: true,
    }),

    events: _.extend({}, InputView.prototype.events, {
      'blur input': function(ev) {
        this._reformat(true);
        this._setOption('value', this.ui.input.val(), { internal: true });
      },

      //Ensure a user can enter ONLY numeric characters across all browsers
      'keydown input,textarea': function(ev) {
        //Some keys are "utility" keys (shift, backspace, etc) which some browsers "allow" in number fields and we
        // don't want those to trigger an event.
        if (isIgnoredKey(ev.key)) {
          return;
        }

        // $ and , should be discarded silently
        if (this._isDiscardedKey(ev.key)) {
          ev.preventDefault();
          return false;
        }

        // ignore '-' if duplicated or
        // if the cursor is not at position 0
        if (ev.key === '-' && (this.ui.input.val().indexOf('-') > -1 || this.ui.input.prop('selectionStart') != 0)) {
          ev.preventDefault();
          return false;
        }

        // ignore '.' if it already exists in the input
        if (ev.key === '.' && this.ui.input.val().indexOf('.') > -1) {
          ev.preventDefault();
          return false;
        }

        // For consistency of UX across all browser platforms, we are suppressing all non-ignored, restricted keys
        if (this._isRestrictedKey(ev.key)) {
          this._triggerInvalidInputEvent(ev.key, NONNUMERIC_ERROR);
          ev.preventDefault();
          return false;
        }

        // block the keypress and show a toast if the keystroke would make the number too large
        var exceedsLengthError = this._exceedsLength(ev.key);
        if (exceedsLengthError) {
          this._triggerInvalidInputEvent(ev.key, exceedsLengthError);
          ev.preventDefault();
          return false;
        }
      },

      'input input,textarea': function(ev) {
        // don't reformat if someone presses e.g. the Shift key
        if (isIgnoredKey(ev.key) && ev.key !== 'Backspace') {
          return;
        }
        this._reformat();
      },

      'paste input,textarea': function(ev) {
        // Prevent users from pasting invalid data.
        var pasteBuffer = (ev.originalEvent.clipboardData || window.clipboardData).getData('text'); //get the past buffer from the clipboard API
        var strippedPasteBuffer = pasteBuffer.replace(/[^\d,\.]/g, '');

        // if the pasteBuffer and stripped pastebuffer are not the same, that means a user tried to paste nonnumeric characters.  We
        // do not allow that, so we are going to swallow their attempt and trigger an invalid input event so the UI can warn the user if desired.
        // If a user is pasting integers, they will be allowed to paste
        if (pasteBuffer !== strippedPasteBuffer) {
          this._triggerInvalidInputEvent(pasteBuffer, NONNUMERIC_ERROR);
          ev.preventDefault();
          return false;
        }

        var exceedsLengthError = this._exceedsLength();
        if (exceedsLengthError) {
          this._triggerInvalidInputEvent(pasteBuffer, exceedsLengthError);
          ev.preventDefault();
          return false;
        }

        this._reformat();
      },
    }),

    _exceedsLength: function(withKey) {
      // get the DOM input element and its value
      var input = this.ui.input;
      var inputVal = input.val();

      // get the cursor start and end position
      var selectionStart = input.prop('selectionStart');
      var selectionEnd = input.prop('selectionEnd');

      // find the position of the decimal point (if any)
      var decimalPos = inputVal.indexOf('.');
      // find numeric data after the decimal point (if any)
      var decimalVal = inputVal.match(/\.(\d+)/);

      // always allow deleting the '-'
      if (selectionEnd - selectionStart > 0 && inputVal.substring(selectionStart, selectionEnd) !== '-') {
        return false;
      }

      // if there is no selection (or selection end/start == 0), allow '-'
      // the keydown event handler will block this if already present or if the cursor is not in the right place
      if (!selectionEnd && !selectionStart && withKey === '-') {
        return false;
      }

      // check if we have a decimal point and the cursor is after it
      // allow another decimal digit if there are less than two
      if (decimalPos > 0 && selectionStart > decimalPos) {
        if (!decimalVal || decimalVal[1].length < 2) {
          return false;
        }
        return NONNUMERIC_ERROR;
      }
      var preCheck = withKey && withKey !== '.';
      // check the number of digits in the whole (non-decimal part)
      // preCheck allows the decimal point even if we have 9 digits already
      if (wholeCurrencyDigits(this.ui.input.val()) > (preCheck ? 8 : 9)) {
        return MAXIMUM_ERROR;
      }
    },

    _reformat: function(blur) {
      // reformat the input element
      // blur: boolean, is this being called from the blur handler?
      var input = this.ui.input;
      var inputVal = input.val();

      if (inputVal === '' || inputVal === '0') {
        return;
      }

      if (inputVal === '-' && blur) {
        input.val('');
        return;
      }

      var isNegative = inputVal.substring(0, 1) === '-';

      // remove preceeding '-' and '0' characters
      // now that we know if the number is negative
      // will re-add the '-' later
      inputVal = inputVal.replace(/^[-0]*/, '');
      var originalLen = inputVal.length;
      // get selection cursor positions
      var caretPos = input.prop('selectionStart');
      var caretEnd = input.prop('selectionEnd');

      if (inputVal.indexOf('.') >= 0) {
        // format a number with a decimal portion
        var decimalPos = inputVal.indexOf('.');
        var leftSide = inputVal.substring(0, decimalPos);
        var rightSide = inputVal.substring(decimalPos);

        // default the whole part to "0" if empty
        leftSide = formatNumber(leftSide) || '0';
        rightSide = stripNumber(rightSide);

        if (blur) {
          // pad with zeroes
          rightSide += '00';
        }

        // trim to most significant 2 decimal digits
        rightSide = rightSide.substring(0, 2);
        inputVal = (isNegative ? '-' : '') + leftSide + '.' + rightSide;
      } else {
        // format a whole number
        inputVal = (isNegative ? '-' : '') + formatNumber(inputVal);
        if (blur) {
          inputVal += '.00';
        }
      }

      // update the element value using jQuery wrapper
      input.val(inputVal);

      var updatedLen = inputVal.length;
      caretPos = updatedLen - originalLen + caretPos;
      caretEnd = updatedLen - originalLen + caretEnd;
      if (isNegative) {
        caretPos--;
      }
      if (isNegative) {
        caretEnd--;
      }

      // update the selection directly on the DOM element
      input[0].setSelectionRange(caretPos, caretEnd);
    },

    _isRestrictedKey: function(key) {
      var ALLOWED_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '.', ',', '$'];

      return ALLOWED_KEYS.indexOf(key) === -1;
    },

    _isDiscardedKey: function(key) {
      var DISCARDED_KEYS = [',', '$'];

      return DISCARDED_KEYS.indexOf(key) !== -1;
    },

    //Trigger an invalid input event. Passes the invalid text and an error message as the arguments
    _triggerInvalidInputEvent: function(invalidText, errorMessage) {
      this.triggerMethod('input:text:invalid', invalidText, errorMessage);
    },
  });

  var InputCurrency = Mn.ItemView.extend(ComponentMixin).extend({
    className: 'ui input currency fluid',

    template: Templates.App['input.currency'],

    options: {
      disabled: false,
      fluid: true,
      readonly: false,
      model: null,
      allowNegative: false,
      value: null,
    },

    internalViewOptions: {
      input: {
        type: InputCurrencyInputView,
      },
    },

    internalViewBindings: {
      input: {
        allowNegative: 'allowNegative',
        disabled: 'disabled',
        fluid: 'fluid',
        readonly: 'readonly',
        value: 'value',
        model: 'model',
      },
    },
  });

  module.exports = InputCurrency;
});

